// Edit log of one scan: immutable ops, each naming its parent, form a DAG; HEAD
// picks the current state, which is the replay of the ops from the root to HEAD.
// Every op carries the resolved pid set it acts on (the "rule" that made it is
// for display only), so replay is set arithmetic and prep/edits.py replays the
// same files to the same state. Pids index the source ply (see build_tiles.py).
import { type PidSet, decodeSet } from './pidset.ts';
import { type Rigid, identity, parseRigid, compose, inverse, isIdentity } from '../align.ts';
import { type PlaneDoc, type ShapeDoc, isPlaneDoc, isShapeDoc } from '../fit/doc.ts';
import type { V3 } from '../math.ts';

export const CLS = ['none', 'floor', 'solid', 'movable'] as const;   // built-in classes 0-3; 4..MAX_CLASS are user classes (class.new)
export type Cls = (typeof CLS)[number];
export const BY = ['frames', 'volume', 'selection'] as const;
export type By = (typeof BY)[number];
export const MAX_ID = 0xffff;          // object / part ids are u16, 0 = none
const NAME = /^[^\u0000-\u001f]{0,80}$/;
export const GROUP_NAME = /^[\w .:+-]{1,60}$/;

interface Base { v: 1; parent: string | null; t: string; msg?: string; rule?: unknown }
export type Op = Base & (
  | { op: 'delete' | 'restore'; set: PidSet }
  | { op: 'classify'; cls: Cls | number; set: PidSet }
  | { op: 'class.new'; cls: number; name: string; color: string }
  | { op: 'class.meta'; cls: number; name: string; color: string }
  | { op: 'class.delete'; cls: number }
  | { op: 'class.color'; cls: number; color: string }   // a built-in class's colour (0-3; user classes have class.meta)
  | { op: 'object.new'; obj: number; name: string; set: PidSet }
  | { op: 'object.assign'; obj: number; set: PidSet }
  | { op: 'object.release'; set: PidSet }
  | { op: 'object.transform'; obj: number; D: Rigid }
  | { op: 'object.meta'; obj: number; name: string }
  | { op: 'object.of'; obj: number; of: string | null; F?: Rigid }
  | { op: 'object.frames'; obj: number; D: Record<string, Rigid> }
  | { op: 'part.new'; part: number; name: string; by: By; set: PidSet }
  | { op: 'part.merge'; part: number }
  | { op: 'group.save'; name: string; set: PidSet; under?: string | null }
  | { op: 'group.move'; name: string; under: string | null }
  | { op: 'group.delete'; name: string }
  | { op: 'tree.group'; name: string; under: string | null; frames: number[] }
  | { op: 'tree.move'; name: string; under: string | null }
  | { op: 'tree.ungroup'; name: string }
  | { op: 'tree.transform'; D: Record<string, Rigid> }
  | { op: 'tree.lock'; frames: number[]; on: boolean }
  | { op: 'tree.mark'; name: string; p: Record<string, MarkVal | null>; w?: number }
  | { op: 'label.new' | 'label.meta'; label: number; name: string; color: string; of?: string }
  | { op: 'label.delete'; label: number }
  | { op: 'label.assign'; label: number; set: PidSet }
  | { op: 'shape.set'; of: string; name: string; shape: ShapeDoc }
  | { op: 'shape.drop'; of: string; name: string }
  | { op: 'plane.set'; name: string; plane: PlaneDoc }
  | { op: 'plane.drop'; name: string }
  | { op: 'type.box'; of: string; box: TypeBox | null }
  | { op: 'type.voxels'; of: string; vox: TypeVoxels | null }
);
export type OpName = Op['op'];
/** A type's bounding box: B places the box (centred, along its own axes) in the world of its reference
 * instance ref, as that instance's points are; s: full edge lengths (m). Each instance shows it through
 * its fit: F_i⁻¹ · F_ref · B. */
export interface TypeBox { ref: string; B: Rigid; s: V3 }
/** A type's voxel model (built on the object page from its fitted instances), drawn in place of each instance's
 * points. Cells of edge size (m) on a grid placed by G in the world of the reference instance ref (as TypeBox's B);
 * cell (i,j,k)'s centre is G · ((i,j,k) + .5) · size, and each instance shows it through its fit, F_i⁻¹ · F_ref · G.
 * n cells, base64: ijk Int16 LE ×3n, rgb u8 ×3n (mean colour), nrm u8 ×2n (mean normal, octahedral). */
export interface TypeVoxels { ref: string; G: Rigid; size: number; n: number; ijk: string; rgb: string; nrm: string }
export const MAX_VOXELS = 200_000;
/** A named point in a frame as an op writes it: the point (the frame's own coordinates), or that with an offset o
 * (m, in the world) its point should meet the others' at, and a weight w (how much its frame gives to meet them:
 * 1 as much as any, 0 not at; default 1). */
export type MarkVal = V3 | { p: V3; o?: V3; w?: number };
export interface Mark { p: V3; o: V3; w: number }
export const MAX_MARK_W = 100;
const isMarkW = (w: unknown) => typeof w === 'number' && w >= 0 && w <= MAX_MARK_W;
const isV3 = (q: unknown) => Array.isArray(q) && q.length === 3 && q.every(v => typeof v === 'number' && Number.isFinite(v));
const isMarkVal = (q: unknown) => isV3(q) || (!!q && typeof q === 'object' && !Array.isArray(q) && Object.keys(q).every(k => k === 'p' || k === 'o' || k === 'w')
  && isV3((q as Mark).p) && ((q as Mark).o === undefined || isV3((q as Mark).o))
  && ((q as Mark).w === undefined || isMarkW((q as Mark).w)));
export const markOf = (q: MarkVal): Mark => (Array.isArray(q) ? { p: [q[0], q[1], q[2]], o: [0, 0, 0], w: 1 }
  : { p: [q.p[0], q.p[1], q.p[2]], o: q.o ? [q.o[0], q.o[1], q.o[2]] : [0, 0, 0], w: q.w ?? 1 });
/** A mark as written: the bare point when it has no offset and the default weight. */
export const markVal = (m: Mark): MarkVal => (m.o.every(v => v === 0) && m.w === 1 ? m.p : { p: m.p, ...(m.o.some(v => v !== 0) ? { o: m.o } : {}), ...(m.w !== 1 ? { w: m.w } : {}) });
export const OPS: OpName[] = ['delete', 'restore', 'classify', 'class.new', 'class.meta', 'class.delete', 'class.color', 'object.new', 'object.assign', 'object.release', 'object.transform',
  'object.meta', 'object.of', 'object.frames', 'part.new', 'part.merge', 'group.save', 'group.move', 'group.delete', 'tree.group', 'tree.move', 'tree.ungroup', 'tree.transform',
  'tree.lock', 'tree.mark', 'label.new', 'label.meta', 'label.delete', 'label.assign', 'shape.set', 'shape.drop', 'plane.set', 'plane.drop', 'type.box', 'type.voxels'];
export const MAX_FRAME = 0xffffffff;   // source frame ids are u32, 0 = unknown
const MAX_TREE_KEYS = 100_000;
const isFrame = (x: unknown) => Number.isInteger(x) && (x as number) >= 1 && (x as number) <= MAX_FRAME;
const isGroupName = (x: unknown) => typeof x === 'string' && GROUP_NAME.test(x);
export const MAX_CLASS = 127;   // a class is 7 bits of the flags byte (bit 0 is deleted)
const isClassId = (x: unknown) => Number.isInteger(x) && (x as number) >= CLS.length && (x as number) <= MAX_CLASS;
export const isClassName = (x: unknown) => typeof x === 'string' && GROUP_NAME.test(x) && !(CLS as readonly string[]).includes(x) && x !== 'unclassified';
export const MAX_LABEL = 255;   // a part label (seat, back, …) is a byte per point, 0 = none
const isLabelId = (x: unknown) => Number.isInteger(x) && (x as number) >= 1 && (x as number) <= MAX_LABEL;
const isColor = (x: unknown) => typeof x === 'string' && /^#[0-9a-fA-F]{6}$/.test(x);
/** A tree.transform key: 'scan', 'g:<group>' or 'f:<frame>'. */
export const isTreeKey = (k: string) => k === 'scan' || (k.startsWith('g:') && GROUP_NAME.test(k.slice(2))) || (/^f:[1-9]\d{0,9}$/.test(k) && isFrame(+k.slice(2)));

const isSet = (s: unknown): s is PidSet => {
  const o = s as PidSet;
  return !!o && o.enc === 'rle1' && Number.isInteger(o.n) && o.n >= 0 && typeof o.b64 === 'string' && /^[A-Za-z0-9+/]*={0,2}$/.test(o.b64);
};
const isId = (x: unknown) => Number.isInteger(x) && (x as number) >= 1 && (x as number) <= MAX_ID;
const isName = (x: unknown) => typeof x === 'string' && NAME.test(x);
/** An object type ("pew"): what an object is an instance of. */
export const isTypeName = (x: unknown) => typeof x === 'string' && x.trim().length > 0 && x.length <= 60 && NAME.test(x);
/** A shape's type, or '' for a one-off shape in the scan. */
const isShapeOf = (x: unknown) => x === '' || isTypeName(x);

const isTypeBox = (x: unknown) => {
  const b = x as TypeBox;
  return !!b && typeof b === 'object' && !Array.isArray(b) && Object.keys(b).every(k => k === 'ref' || k === 'B' || k === 's')
    && isName(b.ref) && b.ref.length > 0 && parseRigid(b.B) !== null && isV3(b.s) && b.s.every(v => v > 0 && v < 1000);
};

const B64 = /^[A-Za-z0-9+/]*={0,2}$/;
const isB64Of = (x: unknown, bytes: number) => typeof x === 'string' && x.length === 4 * Math.ceil(bytes / 3) && B64.test(x);
const isTypeVoxels = (x: unknown) => {
  const v = x as TypeVoxels;
  return !!v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).every(k => ['ref', 'G', 'size', 'n', 'ijk', 'rgb', 'nrm'].includes(k))
    && isName(v.ref) && v.ref.length > 0 && parseRigid(v.G) !== null && typeof v.size === 'number' && v.size >= 0.001 && v.size <= 10
    && Number.isInteger(v.n) && v.n >= 0 && v.n <= MAX_VOXELS && isB64Of(v.ijk, 6 * v.n) && isB64Of(v.rgb, 3 * v.n) && isB64Of(v.nrm, 2 * v.n);
};

/** Validate an op from untrusted JSON; throws with the reason. */
export function parseOp(o: unknown): Op {
  const r = o as Record<string, unknown>;
  const bad = (why: string): never => { throw new Error(`bad op: ${why}`); };
  if (!r || typeof r !== 'object' || Array.isArray(r)) bad('not an object');
  if (r.v !== 1) bad('version');
  if (r.parent !== null && !(typeof r.parent === 'string' && /^[0-9a-f]{16}$/.test(r.parent))) bad('parent');
  if (typeof r.t !== 'string' || r.t.length > 40) bad('t');
  if (r.msg !== undefined && !(typeof r.msg === 'string' && r.msg.length <= 200)) bad('msg');
  const need = (k: string, ok: (x: unknown) => boolean) => { if (!ok(r[k])) bad(k); };
  switch (r.op) {
    case 'delete': case 'restore': case 'object.release': need('set', isSet); break;
    case 'classify': need('cls', x => CLS.includes(x as Cls) || isClassId(x)); need('set', isSet); break;
    case 'class.new': case 'class.meta': need('cls', isClassId); need('name', isClassName); need('color', isColor); break;
    case 'class.delete': need('cls', isClassId); break;
    case 'class.color': need('cls', x => Number.isInteger(x) && (x as number) >= 0 && (x as number) < CLS.length); need('color', isColor); break;
    case 'object.new': need('obj', isId); need('name', isName); need('set', isSet); break;
    case 'object.assign': need('obj', isId); need('set', isSet); break;
    case 'object.transform': need('obj', isId); need('D', x => parseRigid(x) !== null); break;
    case 'object.meta': need('obj', isId); need('name', isName); break;
    case 'object.of': need('obj', isId); need('of', x => x === null || isTypeName(x)); need('F', x => x === undefined || parseRigid(x) !== null); break;
    case 'object.frames': need('obj', isId); need('D', x => !!x && typeof x === 'object' && !Array.isArray(x) && Object.keys(x).length <= MAX_TREE_KEYS
      && Object.entries(x as object).every(([k, d]) => /^f:[1-9]\d{0,9}$/.test(k) && isFrame(+k.slice(2)) && parseRigid(d) !== null)); break;
    case 'part.new': need('part', isId); need('name', isName); need('by', x => BY.includes(x as By)); need('set', isSet); break;
    case 'part.merge': need('part', isId); break;
    case 'group.save': need('name', x => typeof x === 'string' && GROUP_NAME.test(x)); need('set', isSet);
      need('under', x => x === undefined || x === null || isGroupName(x)); break;
    case 'group.move': need('name', isGroupName); need('under', x => x === null || isGroupName(x)); break;
    case 'group.delete': need('name', x => typeof x === 'string' && GROUP_NAME.test(x)); break;
    case 'tree.group': need('name', isGroupName); need('under', x => x === null || isGroupName(x));
      need('frames', x => Array.isArray(x) && x.length <= MAX_TREE_KEYS && x.every(isFrame)); break;
    case 'tree.move': need('name', isGroupName); need('under', x => x === null || isGroupName(x)); break;
    case 'tree.ungroup': need('name', isGroupName); break;
    case 'tree.transform': need('D', x => !!x && typeof x === 'object' && !Array.isArray(x)
      && Object.keys(x).length <= MAX_TREE_KEYS
      && Object.entries(x as object).every(([k, d]) => isTreeKey(k) && parseRigid(d) !== null)); break;
    case 'tree.lock': need('frames', x => Array.isArray(x) && x.length <= MAX_TREE_KEYS && x.every(isFrame)); need('on', x => typeof x === 'boolean'); break;
    case 'tree.mark': need('name', isGroupName); need('p', x => !!x && typeof x === 'object' && !Array.isArray(x) && Object.keys(x).length <= MAX_TREE_KEYS
      && Object.entries(x as object).every(([k, q]) => /^f:[1-9]\d{0,9}$/.test(k) && isFrame(+k.slice(2))
        && (q === null || isMarkVal(q)))); need('w', x => x === undefined || isMarkW(x)); break;
    case 'label.new': case 'label.meta': need('label', isLabelId); need('name', isGroupName); need('color', isColor); need('of', x => x === undefined || isTypeName(x)); break;
    case 'label.delete': need('label', isLabelId); break;
    case 'label.assign': need('label', x => x === 0 || isLabelId(x)); need('set', isSet); break;
    case 'shape.set': need('of', isShapeOf); need('name', isGroupName); need('shape', isShapeDoc); break;
    case 'shape.drop': need('of', isShapeOf); need('name', isGroupName); break;
    case 'plane.set': need('name', isGroupName); need('plane', isPlaneDoc); break;
    case 'plane.drop': need('name', isGroupName); break;
    case 'type.box': need('of', isTypeName); need('box', x => x === null || isTypeBox(x)); break;
    case 'type.voxels': need('of', isTypeName); need('vox', x => x === null || isTypeVoxels(x)); break;
    default: bad('op');
  }
  return r as unknown as Op;
}

// ── state ───────────────────────────────────────────────────────────────

export const DELETED = 1;   // flags bit 0; bits 1-7: class (0-3 the CLS built-ins, 4.. user classes)

/** A movable object. of: the type it is an instance of; F: its fit onto the type's reference
 * instance, in the world (the scene frame the editor shows), for overlaying the instances.
 * W: per source frame, a correction of this object's points from that frame (world, applied
 * before F), so the instance is a good copy of the object; the scan itself is not moved. */
export interface Obj { name: string; D: Rigid; of?: string; F?: Rigid; W?: Map<number, Rigid> }

export interface State {
  N: number;
  /** Ops applied so far: what is worked out from a state can be kept until it changes. */
  ver: number;
  flags: Uint8Array;       // per pid
  obj: Uint16Array;        // per pid, movable object id (0 none)
  part: Uint16Array;       // per pid, split part id (0 = the scan itself)
  objects: Map<number, Obj>;
  parts: Map<number, { name: string; by: By }>;
  groups: Map<string, PidSet>;
  groupParent: Map<string, string>;   // point group -> the group it sits in (absent: top level)
  classes: Map<number, { name: string; color: string }>;   // user classes (ids 4..MAX_CLASS)
  clsColors: Map<number, string>;   // built-in classes (0-3) given their own colour (class.color)
  /** Per pid, a part label within its object (seat, back, …; 0 none). Only meaningful inside an object:
   * a point leaving its object (release, or assigned to another) loses its label. */
  label: Uint8Array;
  /** of: the object type the label belongs to (seat of a chair, not of a table); absent on labels from
   * before labels had a type, which every type sees. */
  labels: Map<number, { name: string; color: string; of?: string }>;
  /** Shapes fitted to a type's part label (shapeKey(type, label name)), or one-offs in this scan (of '', by
   * their own name: see src/fit/doc.ts); t: when (newest wins across scans). */
  shapes: Map<string, { of: string; name: string; shape: ShapeDoc; t: string }>;
  /** Planes kept in this scan, by name (src/fit/doc.ts PlaneDoc); t: when. */
  planes: Map<string, { plane: PlaneDoc; t: string }>;
  /** A type's bounding box as last set here (null: cleared); t: when (newest wins across scans). */
  boxes: Map<string, { box: TypeBox | null; t: string }>;
  /** A type's voxel model as last set here (null: cleared); t: when (newest wins across scans). */
  voxels: Map<string, { vox: TypeVoxels | null; t: string }>;
  tree: Tree;
}

/** The alignment tree: scan > groups (nested) > frames, each with a rigid correction D in the
 * scan's file frame. A frame's points are drawn at E(f) = D_scan · D_g1 · … · D_gk · D_f
 * (outermost group first). Only tree.transform moves points: grouping, moving and ungrouping
 * re-express the Ds so nothing jumps. */
export interface Tree {
  scanD: Rigid;
  groups: Map<string, { parent: string | null; D: Rigid }>;
  frameGroup: Map<number, string>;   // frame -> its group (absent: directly under the scan)
  frameD: Map<number, Rigid>;        // absent: identity
  /** Frames locked in place: aligning frames neither fits them nor carries them along (they move only with their group or the scan). */
  locked: Set<number>;
  /** Named points: name -> frame -> a point in that frame's own coordinates (drawn at E(f) · p), its offset and
   * weight (MarkVal). Frames with a point of the same name (in this scan or another) are pulled together until
   * those points (each plus its offset) meet. */
  marks: Map<string, Map<number, Mark>>;
  /** A named point's own weight (absent: 1), multiplied into each of its frames' weights. Kept per scan, the
   * editor sets it alike in every scan with the name. Dropped with the name's last point. */
  markW: Map<string, number>;
}

export const emptyState = (N: number): State => ({
  N, ver: 0, flags: new Uint8Array(N), obj: new Uint16Array(N), part: new Uint16Array(N),
  objects: new Map(), parts: new Map(), groups: new Map(), groupParent: new Map(), classes: new Map(), clsColors: new Map(),
  label: new Uint8Array(N), labels: new Map(), shapes: new Map(), planes: new Map(), boxes: new Map(), voxels: new Map(),
  tree: { scanD: identity(), groups: new Map(), frameGroup: new Map(), frameD: new Map(), locked: new Set(), marks: new Map(), markW: new Map() },
});

/** Transform from a group's frame to the scan file frame: D_scan · … · D_group (null: D_scan). */
export function chainOf(t: Tree, g: string | null): Rigid {
  const path: Rigid[] = [];
  for (let n = g, k = 0; n !== null; k++) {
    const x = t.groups.get(n);
    if (!x || k > t.groups.size) throw new Error(`bad group ${n}`);
    path.push(x.D); n = x.parent;
  }
  let M = t.scanD;
  for (let i = path.length - 1; i >= 0; i--) M = compose(M, path[i]);
  return M;
}

/** E(f) for one frame. */
export const frameTransform = (t: Tree, f: number): Rigid => compose(chainOf(t, t.frameGroup.get(f) ?? null), t.frameD.get(f) ?? identity());

/** True if any frame is drawn somewhere else than its source position. */
export function treeMoves(t: Tree): boolean {
  if (!isIdentity(t.scanD)) return true;
  for (const g of t.groups.values()) if (!isIdentity(g.D)) return true;
  for (const d of t.frameD.values()) if (!isIdentity(d)) return true;
  return false;
}

/** Is a an ancestor of (or equal to) b? */
function above(t: Tree, a: string, b: string | null) {
  for (let n = b, k = 0; n !== null && k <= t.groups.size; k++) { if (n === a) return true; n = t.groups.get(n)!.parent; }
  return false;
}

function applyTree(s: State, op: Op) {
  const t = s.tree;
  const need = (g: string | null) => { if (g !== null && !t.groups.has(g)) throw new Error(`no group ${g}`); };
  switch (op.op) {
    case 'tree.group': {
      need(op.under);
      const old = t.groups.get(op.name);
      if (!old) {
        if (op.under === op.name) throw new Error('a group cannot contain itself');
        t.groups.set(op.name, { parent: op.under, D: identity() });
      } else {
        if (old.parent !== op.under) throw new Error(`group ${op.name} is under ${old.parent ?? 'the scan'}: use tree.move`);
        // frames dropped from the list go up to the group's parent, staying where they are
        const keep = new Set(op.frames), up = chainOf(t, old.parent), here = chainOf(t, op.name);
        for (const [f, g] of [...t.frameGroup]) if (g === op.name && !keep.has(f)) moveFrame(t, f, here, op.under, up);
      }
      const here = chainOf(t, op.name);
      for (const f of op.frames) {
        const g = t.frameGroup.get(f) ?? null;
        if (g !== op.name) moveFrame(t, f, chainOf(t, g), op.name, here);
      }
      break;
    }
    case 'tree.move': {
      const g = t.groups.get(op.name);
      if (!g) throw new Error(`no group ${op.name}`);
      need(op.under);
      if (above(t, op.name, op.under)) throw new Error(`${op.name} would contain itself`);
      g.D = compose(inverse(chainOf(t, op.under)), compose(chainOf(t, g.parent), g.D));
      g.parent = op.under;
      break;
    }
    case 'tree.ungroup': {
      const g = t.groups.get(op.name);
      if (!g) throw new Error(`no group ${op.name}`);
      for (const c of t.groups.values()) if (c.parent === op.name) { c.parent = g.parent; c.D = compose(g.D, c.D); }
      for (const [f, n] of [...t.frameGroup]) {
        if (n !== op.name) continue;
        t.frameD.set(f, compose(g.D, t.frameD.get(f) ?? identity()));
        if (g.parent === null) t.frameGroup.delete(f); else t.frameGroup.set(f, g.parent);
      }
      t.groups.delete(op.name);
      break;
    }
    case 'tree.transform':
      for (const k of Object.keys(op.D)) if (k.startsWith('g:')) need(k.slice(2));
      for (const [k, d] of Object.entries(op.D)) {
        const D = parseRigid(d)!;
        if (k === 'scan') t.scanD = D;
        else if (k.startsWith('g:')) t.groups.get(k.slice(2))!.D = D;
        else t.frameD.set(+k.slice(2), D);
      }
      break;
    case 'tree.lock':
      for (const f of op.frames) if (op.on) t.locked.add(f); else t.locked.delete(f);
      break;
    case 'tree.mark': {
      const m = t.marks.get(op.name) ?? new Map<number, Mark>();
      for (const [k, q] of Object.entries(op.p)) if (q) m.set(+k.slice(2), markOf(q)); else m.delete(+k.slice(2));
      if (op.w !== undefined) { if (op.w === 1) t.markW.delete(op.name); else t.markW.set(op.name, op.w); }
      if (m.size) t.marks.set(op.name, m); else { t.marks.delete(op.name); t.markW.delete(op.name); }
      break;
    }
  }
}

/** Put frame f (now under a group whose chain is `from`) under group g (chain `to`), unmoved. */
function moveFrame(t: Tree, f: number, from: Rigid, g: string | null, to: Rigid) {
  t.frameD.set(f, compose(inverse(to), compose(from, t.frameD.get(f) ?? identity())));
  if (g === null) t.frameGroup.delete(f); else t.frameGroup.set(f, g);
}

/** A point's class name (built-in or user). */
export const clsOf = (s: State, pid: number): string => { const k = s.flags[pid] >> 1; return k < CLS.length ? CLS[k] : s.classes.get(k)?.name ?? '?'; };

/** Next free label id (max + 1), or MAX_LABEL + 1 when full. */
export const nextLabelId = (s: State) => Math.max(0, ...s.labels.keys()) + 1;

/** Per label id, the types whose instances' points carry it. */
export function labelTypes(s: State) {
  const out = new Map<number, Set<string>>();
  let last = -1;
  for (let p = 0; p < s.N; p++) {
    const l = s.label[p];
    if (!l) continue;
    const key = (l << 16) | s.obj[p];
    if (key === last) continue;
    last = key;
    const of = s.objects.get(s.obj[p])?.of;
    if (of) (out.get(l) ?? out.set(l, new Set()).get(l)!).add(of);
  }
  return out;
}

/** The labels a type sees in a scan: its own, and any others its instances' points carry (untyped ones from
 * before labels had a type, or one an instance brought along when its type changed). */
export function typeLabels(s: State, type: string, use = labelTypes(s)) {
  return new Map([...s.labels].filter(([id, l]) => l.of === type || use.get(id)?.has(type)));
}

/** An untyped label that only this type's points carry (or none's): the type can take it over (label.meta with of). */
export const claimable = (s: State, id: number, type: string, use: Map<number, Set<string>>) => {
  const u = use.get(id);
  return s.labels.get(id)?.of === undefined && (!u || (u.size === 1 && u.has(type)));
};

/** Next free user class id (max + 1, like objects), or MAX_CLASS + 1 when full. */
export const nextClassId = (s: State) => Math.max(CLS.length - 1, ...s.classes.keys()) + 1;

/** Apply one op in place. Throws if it doesn't fit the state (unknown id, pid >= N). */
export function apply(s: State, op: Op) {
  s.ver++;
  const pids = 'set' in op ? decodeSet(op.set, s.N) : null!;
  switch (op.op) {
    case 'delete': for (const p of pids) s.flags[p] |= DELETED; break;
    case 'restore': for (const p of pids) s.flags[p] &= ~DELETED; break;
    case 'classify': {
      const id = typeof op.cls === 'number' ? op.cls : CLS.indexOf(op.cls);
      if (typeof op.cls === 'number' && !s.classes.has(id)) throw new Error(`no class ${id}`);
      for (const p of pids) s.flags[p] = (s.flags[p] & DELETED) | (id << 1);
      break;
    }
    case 'class.new':
      if (s.classes.has(op.cls)) throw new Error(`class ${op.cls} exists`);
      s.classes.set(op.cls, { name: op.name, color: op.color.toLowerCase() });
      break;
    case 'class.meta':
      if (!s.classes.has(op.cls)) throw new Error(`no class ${op.cls}`);
      s.classes.set(op.cls, { name: op.name, color: op.color.toLowerCase() });
      break;
    case 'class.color': s.clsColors.set(op.cls, op.color.toLowerCase()); break;
    case 'class.delete': {
      if (!s.classes.delete(op.cls)) throw new Error(`no class ${op.cls}`);
      for (let p = 0; p < s.N; p++) if (s.flags[p] >> 1 === op.cls) s.flags[p] &= DELETED;   // its points become unclassified
      break;
    }
    case 'object.new':
      if (s.objects.has(op.obj)) throw new Error(`object ${op.obj} exists`);
      s.objects.set(op.obj, { name: op.name, D: identity() });
      for (const p of pids) { if (s.obj[p] !== op.obj) s.label[p] = 0; s.obj[p] = op.obj; }
      break;
    case 'object.assign':
      if (!s.objects.has(op.obj)) throw new Error(`no object ${op.obj}`);
      for (const p of pids) { if (s.obj[p] !== op.obj) s.label[p] = 0; s.obj[p] = op.obj; }
      break;
    case 'object.release': for (const p of pids) { s.obj[p] = 0; s.label[p] = 0; } break;
    case 'object.transform': {
      const o = s.objects.get(op.obj);
      if (!o) throw new Error(`no object ${op.obj}`);
      o.D = parseRigid(op.D)!;
      break;
    }
    case 'object.meta': {
      const o = s.objects.get(op.obj);
      if (!o) throw new Error(`no object ${op.obj}`);
      o.name = op.name;
      break;
    }
    case 'object.of': {
      const o = s.objects.get(op.obj);
      if (!o) throw new Error(`no object ${op.obj}`);
      if (op.of === null) { delete o.of; delete o.F; } else { o.of = op.of; o.F = op.F ? parseRigid(op.F)! : identity(); }
      break;
    }
    case 'object.frames': {   // replaces the object's whole set of frame corrections ({} clears them)
      const o = s.objects.get(op.obj);
      if (!o) throw new Error(`no object ${op.obj}`);
      const W = new Map(Object.entries(op.D).map(([k, d]) => [+k.slice(2), parseRigid(d)!]));
      if (W.size) o.W = W; else delete o.W;
      break;
    }
    case 'part.new':
      if (s.parts.has(op.part)) throw new Error(`part ${op.part} exists`);
      s.parts.set(op.part, { name: op.name, by: op.by });
      for (const p of pids) s.part[p] = op.part;
      break;
    case 'part.merge':
      if (!s.parts.delete(op.part)) throw new Error(`no part ${op.part}`);
      for (let p = 0; p < s.N; p++) if (s.part[p] === op.part) s.part[p] = 0;
      break;
    case 'group.save':
      if (op.under !== undefined) setGroupParent(s, op.name, op.under);
      s.groups.set(op.name, op.set);
      break;
    case 'group.move':
      if (!s.groups.has(op.name)) throw new Error(`no group ${op.name}`);
      setGroupParent(s, op.name, op.under);
      break;
    case 'group.delete': {
      // its sub-groups move up to its parent
      const up = s.groupParent.get(op.name);
      for (const [g, p] of [...s.groupParent]) if (p === op.name) { if (up === undefined) s.groupParent.delete(g); else s.groupParent.set(g, up); }
      s.groupParent.delete(op.name);
      s.groups.delete(op.name);
      break;
    }
    case 'tree.group': case 'tree.move': case 'tree.ungroup': case 'tree.transform': case 'tree.lock': case 'tree.mark': applyTree(s, op); break;
    case 'label.new':
      if (s.labels.has(op.label)) throw new Error(`label ${op.label} exists`);
      s.labels.set(op.label, { name: op.name, color: op.color.toLowerCase(), ...(op.of !== undefined ? { of: op.of } : {}) });
      break;
    case 'label.meta': {   // without of, the label keeps its type
      const L = s.labels.get(op.label);
      if (!L) throw new Error(`no label ${op.label}`);
      const of = op.of ?? L.of;
      s.labels.set(op.label, { name: op.name, color: op.color.toLowerCase(), ...(of !== undefined ? { of } : {}) });
      break;
    }
    case 'label.delete':
      if (!s.labels.delete(op.label)) throw new Error(`no label ${op.label}`);
      for (let p = 0; p < s.N; p++) if (s.label[p] === op.label) s.label[p] = 0;
      break;
    case 'label.assign':
      if (op.label && !s.labels.has(op.label)) throw new Error(`no label ${op.label}`);
      for (const p of pids) s.label[p] = op.label;
      break;
    case 'shape.set': s.shapes.set(shapeKey(op.of, op.name), { of: op.of, name: op.name, shape: op.shape, t: op.t }); break;
    case 'shape.drop':
      if (!s.shapes.delete(shapeKey(op.of, op.name))) throw new Error(`no shape ${op.of}/${op.name}`);
      break;
    case 'plane.set': s.planes.set(op.name, { plane: op.plane, t: op.t }); break;
    case 'plane.drop':
      if (!s.planes.delete(op.name)) throw new Error(`no plane ${op.name}`);
      break;
    case 'type.box': s.boxes.set(op.of, { box: op.box, t: op.t }); break;
    case 'type.voxels': s.voxels.set(op.of, { vox: op.vox, t: op.t }); break;
  }
}

/** Key of a type's shape for a label name (names have no control characters). */
export const shapeKey = (of: string, name: string) => `${of}\u0000${name}`;

export function replay(N: number, ops: Op[]): State {
  const s = emptyState(N);
  for (const op of ops) apply(s, op);
  return s;
}

function setGroupParent(s: State, name: string, under: string | null) {
  if (under === null) { s.groupParent.delete(name); return; }
  if (!s.groups.has(under)) throw new Error(`no group ${under}`);
  for (let g: string | undefined = under, k = 0; g !== undefined && k <= s.groups.size; k++) {
    if (g === name) throw new Error(`group ${name} would contain itself`);
    g = s.groupParent.get(g);
  }
  s.groupParent.set(name, under);
}

/** A point group and every group below it. */
export function groupAndBelow(s: State, name: string): string[] {
  const out = [name];
  for (let i = 0; i < out.length && out.length <= s.groups.size; i++)
    for (const [g, p] of s.groupParent) if (p === out[i] && !out.includes(g)) out.push(g);
  return out;
}

/** Where a point is published: null if deleted, else its object, part, or the scan. */
export function outputOf(s: State, pid: number): { kind: 'scan' } | { kind: 'object' | 'part'; id: number } | null {
  if (s.flags[pid] & DELETED) return null;
  if (s.obj[pid]) return { kind: 'object', id: s.obj[pid] };
  if (s.part[pid]) return { kind: 'part', id: s.part[pid] };
  return { kind: 'scan' };
}

/** Next free object / part id (max + 1, so ids stay distinct across branches that share a past). */
export const nextId = (m: Map<number, unknown>) => Math.min(MAX_ID, Math.max(0, ...m.keys()) + 1);

// ── graph ───────────────────────────────────────────────────────────────

export interface Node { id: string; parent: string | null }

/** Ids from the root to head (inclusive), or [] for a null head. Throws on a missing op or a cycle. */
export function ancestors(nodes: Map<string, Node>, head: string | null): string[] {
  const out: string[] = [];
  for (let id = head; id !== null;) {
    const n = nodes.get(id);
    if (!n) throw new Error(`missing op ${id}`);
    if (out.length > nodes.size) throw new Error('cycle in edit graph');
    out.push(id);
    id = n.parent;
  }
  return out.reverse();
}

/** Children of each op (key null: the roots), each list in the order given. */
export function children(nodes: Iterable<Node>): Map<string | null, string[]> {
  const m = new Map<string | null, string[]>();
  for (const n of nodes) {
    const l = m.get(n.parent);
    if (l) l.push(n.id); else m.set(n.parent, [n.id]);
  }
  return m;
}

/** State arrays as bytes (flags, obj LE, part LE, label): what parity tests hash. */
export function stateBytes(s: State): Uint8Array {
  const N = s.N, b = new Uint8Array(6 * N), d = new DataView(b.buffer);
  b.set(s.flags);
  b.set(s.label, 5 * N);
  for (let i = 0; i < N; i++) { d.setUint16(N + 2 * i, s.obj[i], true); d.setUint16(3 * N + 2 * i, s.part[i], true); }
  return b;
}

/** Metadata as plain JSON (sorted keys), for tests and the UI. */
export function stateMeta(s: State) {
  const byKey = <K, V>(m: Map<K, V>) => [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return {
    objects: Object.fromEntries(byKey(s.objects).map(([k, o]) => [String(k), { name: o.name, D: o.D, ...(o.of !== undefined ? { of: o.of, F: o.F } : {}),
      ...(o.W ? { W: Object.fromEntries([...o.W].sort((a, b) => a[0] - b[0]).map(([f, d]) => [String(f), d])) } : {}) }])),
    parts: Object.fromEntries(byKey(s.parts).map(([k, p]) => [String(k), { name: p.name, by: p.by }])),
    groups: Object.fromEntries(byKey(s.groups).map(([k, g]) => [k, g.n])),
    ...(s.groupParent.size ? { groupParents: Object.fromEntries(byKey(s.groupParent)) } : {}),
    ...(s.classes.size ? { classes: Object.fromEntries(byKey(s.classes).map(([k, c]) => [String(k), { name: c.name, color: c.color }])) } : {}),
    ...(s.clsColors.size ? { clsColors: Object.fromEntries(byKey(s.clsColors).map(([k, c]) => [String(k), c])) } : {}),
    ...(s.labels.size ? { labels: Object.fromEntries(byKey(s.labels).map(([k, c]) => [String(k), { name: c.name, color: c.color, ...(c.of !== undefined ? { of: c.of } : {}) }])) } : {}),
    ...(s.shapes.size ? { shapes: Object.fromEntries([...s.shapes.values()].map(x => [`${x.of}/${x.name}`, { of: x.of, name: x.name, shape: x.shape }] as const)
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))) } : {}),
    ...(s.planes.size ? { planes: Object.fromEntries(byKey(s.planes).map(([k, x]) => [k, x.plane])) } : {}),
    ...(s.boxes.size ? { boxes: Object.fromEntries(byKey(s.boxes).map(([k, x]) => [k, x.box])) } : {}),
    ...(s.voxels.size ? { voxels: Object.fromEntries(byKey(s.voxels).map(([k, x]) => [k, x.vox])) } : {}),
    ...(treeEmpty(s.tree) ? {} : { tree: treeMeta(s.tree) }),
  };
}

const treeEmpty = (t: Tree) => !t.groups.size && !t.frameGroup.size && !t.frameD.size && !t.locked.size && !t.marks.size && isIdentity(t.scanD);

/** The tree as plain JSON (sorted keys). */
export function treeMeta(t: Tree) {
  const frames = [...new Set([...t.frameGroup.keys(), ...t.frameD.keys()])].sort((a, b) => a - b);
  return {
    scanD: t.scanD,
    groups: Object.fromEntries([...t.groups].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([k, g]) => [k, { parent: g.parent, D: g.D }])),
    frames: Object.fromEntries(frames.map(f => [String(f), { group: t.frameGroup.get(f) ?? null, D: t.frameD.get(f) ?? identity() }])),
    ...(t.locked.size ? { locked: [...t.locked].sort((a, b) => a - b) } : {}),
    ...(t.marks.size ? { marks: Object.fromEntries([...t.marks].sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([k, m]) => [k, Object.fromEntries([...m].sort((a, b) => a[0] - b[0]).map(([f, q]) => [String(f), markVal(q)]))])) } : {}),
    ...(t.markW.size ? { markW: Object.fromEntries([...t.markW].sort((a, b) => (a[0] < b[0] ? -1 : 1))) } : {}),
  };
}
